import { Attendance, AttendanceStatusEnum, ChannelTypeEnum, UtilHelper } from 'lib-trend-core';

/** Actions the user can pin to the bar; Encerrar, search and the panel toggle are fixed. */
export type ChatHeaderPinnableId =
  | 'template'
  | 'transfer'
  | 'whisper'
  | 'pause'
  | 'call'
  | 'scheduled'
  | 'history'
  | 'timeline'
  | 'export'
  | 'ai'
  | 'crm'
  | 'block';

/** Everything the header can ask the chat panel to do. */
export type ChatHeaderActionId =
  | ChatHeaderPinnableId
  | 'close'
  | 'panel'
  | 'search'
  | 'searchSubmit'
  | 'searchPrevious'
  | 'searchNext'
  | 'searchOlder';

export type ChatHeaderSection = 'attendance' | 'conversation' | 'automation' | 'contact';

export const CHAT_HEADER_SECTIONS: ChatHeaderSection[] = ['attendance', 'conversation', 'automation', 'contact'];

/** What decides which actions exist for the attendance at hand. */
export interface ChatHeaderContext {
  attendance: Attendance;
  privileged: boolean;
  canTransfer: boolean;
  canExport: boolean;
  canBlock: boolean;
  aiModule: boolean;
  aiActive: boolean;
  callModule: boolean;
  crmIntegration: boolean;
}

/** An action resolved for the attendance at hand; `label` and `short` are i18n keys. */
export interface ChatHeaderAction {
  id: ChatHeaderPinnableId;
  section: ChatHeaderSection;
  icon: string;
  label: string;
  short: string;
  highlight: boolean;
  promoted: boolean;
  danger: boolean;
  pressed?: boolean;
}

interface ActionLook {
  icon: string;
  label: string;
  short: string;
}

interface ActionDefinition extends ActionLook {
  id: ChatHeaderPinnableId;
  section: ChatHeaderSection;
  available: (ctx: ChatHeaderContext) => boolean;
  on?: ActionLook & { when: (ctx: ChatHeaderContext) => boolean };
  promoteWhenOn?: boolean;
  highlightWhenOn?: boolean;
  toggle?: boolean;
  danger?: boolean;
}

const KEY = 'attendance.chatHeader.action.';

const isOpen = (ctx: ChatHeaderContext): boolean => ctx.attendance?.status !== AttendanceStatusEnum.CLOSED;
const isPaused = (ctx: ChatHeaderContext): boolean => ctx.attendance?.status === AttendanceStatusEnum.PAUSED;
const onChannel = (ctx: ChatHeaderContext, type: ChannelTypeEnum): boolean => ctx.attendance?.channel?.type === type;

export function isContactBlocked(attendance: Attendance): boolean {
  return !!attendance?.contact?.blockedChannels?.includes(attendance?.channel?._id);
}

/** Cloud API and RCS carry their 24-hour window in `expiredAt`; every other channel has none. */
export function conversationWindowOf(attendance: Attendance): 'open' | 'expired' | null {
  const windowed = [ChannelTypeEnum.CLOUD_API, ChannelTypeEnum.RCS_API].includes(attendance?.channel?.type);
  if (!windowed || !attendance?.expiredAt) return null;
  return UtilHelper.formatExpireIn(attendance.expiredAt).isExpired ? 'expired' : 'open';
}

const ACTIONS: ActionDefinition[] = [
  {
    id: 'template', section: 'attendance', icon: 'ri-layout-4-line', label: KEY + 'template', short: 'attendance.chat.template',
    available: (ctx) => isOpen(ctx) && onChannel(ctx, ChannelTypeEnum.CLOUD_API),
  },
  {
    id: 'transfer', section: 'attendance', icon: 'ri-user-shared-line', label: KEY + 'transfer', short: 'attendance.chat.transfer',
    available: (ctx) => isOpen(ctx) && ctx.canTransfer,
  },
  {
    id: 'whisper', section: 'attendance', icon: 'ri-chat-private-line', label: KEY + 'whisper', short: 'attendance.whisper.action',
    available: (ctx) => isOpen(ctx) && ctx.privileged,
  },
  {
    id: 'pause', section: 'attendance', icon: 'ri-pause-circle-line', label: KEY + 'pause', short: 'attendance.chat.pause',
    available: isOpen,
    on: { when: isPaused, icon: 'ri-play-circle-line', label: KEY + 'continue', short: 'attendance.chat.continue' },
    promoteWhenOn: true,
  },
  {
    id: 'call', section: 'automation', icon: 'ri-phone-line', label: KEY + 'call', short: 'attendance.chat.call',
    available: (ctx) => isOpen(ctx) && ctx.callModule && onChannel(ctx, ChannelTypeEnum.Z_API),
  },
  {
    id: 'scheduled', section: 'conversation', icon: 'ri-calendar-check-line', label: 'attendance.chat.scheduledMessages', short: KEY + 'scheduledShort',
    available: isOpen,
  },
  {
    id: 'history', section: 'conversation', icon: 'ri-history-line', label: 'attendance.chat.attendanceHistory', short: 'kanban.cardDetails.tabHistory',
    available: () => true,
  },
  {
    id: 'timeline', section: 'conversation', icon: 'ri-timeline-view', label: 'attendance.chat.attendanceTimeline', short: KEY + 'timelineShort',
    available: () => true,
  },
  {
    id: 'export', section: 'conversation', icon: 'ri-upload-2-line', label: 'attendance.chat.exportConversation', short: 'report.list.export',
    available: (ctx) => ctx.canExport,
  },
  {
    id: 'ai', section: 'automation', icon: 'ri-robot-2-line', label: KEY + 'aiOn', short: KEY + 'aiShort',
    available: (ctx) => isOpen(ctx) && ctx.aiModule,
    on: { when: (ctx) => ctx.aiActive, icon: 'ri-robot-2-line', label: KEY + 'aiOff', short: KEY + 'aiShort' },
    highlightWhenOn: true,
    toggle: true,
  },
  {
    id: 'crm', section: 'automation', icon: 'ri-plug-2-line', label: KEY + 'crm', short: 'layout.sidebar.crm',
    available: (ctx) => isOpen(ctx) && ctx.crmIntegration,
  },
  {
    id: 'block', section: 'contact', icon: 'ri-forbid-line', label: KEY + 'block', short: 'shared.contactListModal.block',
    available: (ctx) => isOpen(ctx) && ctx.canBlock,
    on: { when: (ctx) => isContactBlocked(ctx.attendance), icon: 'ri-forbid-line', label: KEY + 'unblock', short: 'shared.contactListModal.unblock' },
    danger: true,
  },
];

function present(def: ActionDefinition, ctx: ChatHeaderContext): ChatHeaderAction {
  const on = !!def.on?.when(ctx);
  const look: ActionLook = on ? def.on : def;
  return {
    id: def.id,
    section: def.section,
    icon: look.icon,
    label: look.label,
    short: look.short,
    highlight: on && !!(def.promoteWhenOn || def.highlightWhenOn),
    promoted: on && !!def.promoteWhenOn,
    danger: !on && !!def.danger,
    pressed: def.toggle ? on : undefined,
  };
}

export function resolveChatHeaderActions(ctx: ChatHeaderContext): ChatHeaderAction[] {
  return ACTIONS.filter((def) => def.available(ctx)).map((def) => present(def, ctx));
}

const DEFAULT_PIN_ORDER: ChatHeaderPinnableId[] = ['template', 'transfer', 'pause', 'call', 'whisper', 'history'];
const DEFAULT_PIN_COUNT = 3;

/** Three pinned by default; when one is not available here, the next in line takes its place. */
export function defaultChatHeaderPins(actions: ChatHeaderAction[]): ChatHeaderPinnableId[] {
  const available = new Set(actions.map((action) => action.id));
  return DEFAULT_PIN_ORDER.filter((id) => available.has(id)).slice(0, DEFAULT_PIN_COUNT);
}

/** Pinned actions in display order; the action that unblocks the attendance always comes first. */
export function chatHeaderBarActions(actions: ChatHeaderAction[], pins: ChatHeaderPinnableId[]): ChatHeaderAction[] {
  const pinned = pins
    .map((id) => actions.find((action) => action.id === id))
    .filter((action): action is ChatHeaderAction => !!action);
  const promoted = actions.find((action) => action.promoted);
  return promoted ? [promoted, ...pinned.filter((action) => action !== promoted)] : pinned;
}

/** Fixed sizes of the row, in px: the platform's avatar-sm, small btn-icon and spacing scale. */
const AVATAR = 48;
const ICON_BUTTON = 30;
const ACTION_GAP = 6;
const IDENTITY_GAP = 12;
const LABEL_CHROME = 48;
const CLOSE_LABEL = 'attendance.chat.close';

/** Measures the rendered text of an i18n key, in px. */
export type ChatHeaderMeasure = (i18nKey: string) => number;

export interface ChatHeaderLayout {
  key: string;
  wide: boolean;
  inline: boolean;
  olderLabeled: boolean;
  labeled: boolean;
  promotedLabeled: boolean;
  closeLabeled: boolean;
  statusCount: number;
  shown: ChatHeaderAction[];
  hidden: ChatHeaderAction[];
}

interface RowMetrics {
  wide: boolean;
  inline: boolean;
  room: number;
}

interface Fit {
  labeled: boolean;
  count: number;
  leftover: number;
}

/** Room left for the pinned actions once avatar, name and the fixed buttons are placed. */
function rowMetrics(width: number): RowMetrics {
  const wide = width >= 760;
  const inline = width >= 400;
  const padding = wide ? 48 : 24;
  const identityMin = width >= 640 ? 150 : width >= 560 ? 130 : 110;
  const search = inline ? ICON_BUTTON + ACTION_GAP : 0;
  const panel = inline ? ACTION_GAP * 2 + 1 + ICON_BUTTON : 0;
  const fixed = padding + AVATAR + IDENTITY_GAP * 2 + identityMin + ICON_BUTTON + ACTION_GAP + ICON_BUTTON + search + panel;
  return { wide, inline, room: Math.max(0, width - fixed) };
}

function labelExtra(i18nKey: string, measure: ChatHeaderMeasure): number {
  return measure(i18nKey) + LABEL_CHROME - ICON_BUTTON;
}

/** Each pinned button is counted with the gap that follows it, up to Encerrar. */
function fitPinned(width: number, actions: ChatHeaderAction[], row: RowMetrics, measure: ChatHeaderMeasure): Fit {
  const labeledWidth = actions.reduce((sum, action) => sum + measure(action.short) + LABEL_CHROME + ACTION_GAP, 0);
  if (width >= 1100 && actions.length && labeledWidth + labelExtra(CLOSE_LABEL, measure) <= row.room) {
    return { labeled: true, count: actions.length, leftover: row.room - labeledWidth };
  }
  const minimum = actions[0]?.promoted ? 1 : 0;
  const count = Math.max(minimum, Math.min(actions.length, Math.floor(row.room / (ICON_BUTTON + ACTION_GAP))));
  return { labeled: false, count, leftover: row.room - count * (ICON_BUTTON + ACTION_GAP) };
}

/** Space left after the icons goes first to the promoted action's label, then to Encerrar's. */
function extraLabels(fit: Fit, shown: ChatHeaderAction[], width: number, measure: ChatHeaderMeasure): Pick<ChatHeaderLayout, 'promotedLabeled' | 'closeLabeled'> {
  if (fit.labeled) return { promotedLabeled: true, closeLabeled: true };
  const promoted = shown.find((action) => action.promoted);
  const promotedExtra = promoted ? labelExtra(promoted.short, measure) : 0;
  const promotedLabeled = !!promoted && fit.leftover >= promotedExtra;
  const leftover = promotedLabeled ? fit.leftover - promotedExtra : fit.leftover;
  return { promotedLabeled, closeLabeled: width >= 560 && leftover >= labelExtra(CLOSE_LABEL, measure) };
}

/** Which pinned actions fit the header at this width, and which elements carry their labels. */
export function computeChatHeaderLayout(width: number, actions: ChatHeaderAction[], measure: ChatHeaderMeasure): ChatHeaderLayout {
  const row = rowMetrics(width);
  const fit = fitPinned(width, actions, row, measure);
  const shown = actions.slice(0, fit.count);
  const labels = extraLabels(fit, shown, width, measure);
  const statusCount = width >= 1240 ? 2 : 1;
  const olderLabeled = width >= 640;
  return {
    key: [row.wide, row.inline, olderLabeled, fit.labeled, labels.promotedLabeled, labels.closeLabeled, statusCount, shown.map((action) => action.id)].join('|'),
    wide: row.wide,
    inline: row.inline,
    olderLabeled,
    labeled: fit.labeled,
    statusCount,
    shown,
    hidden: actions.slice(fit.count),
    ...labels,
  };
}
